import type { components } from "@/lib/api/generated/schema";

type Badge = components["schemas"]["SalesSquadBadge"];
type Response = components["schemas"]["SalesSquadOrderBadges"];

/** Nhãn của một đơn: người xem thấy tên bạn sale, hoặc nhãn tiểu đội (null = giữ tên bạn sale). */
export type OrderBadge = { showSellerName: boolean; badge: Badge | null };

/** Trần mã đơn một lượt hỏi — khớp `salessquad.MaxBadgeOrders` ở máy chủ. */
export const MAX_BADGE_ORDERS = 200;

type Waiter = { resolve: (v: OrderBadge) => void; reject: (e: unknown) => void };

/**
 * Gom mã đơn của mọi ô trong cùng một nhịp vẽ (lịch tuần vài trăm ô, danh sách đơn 50 dòng)
 * thành một lần gọi mỗi lô 200 mã — thay vì mỗi ô một lần. Mã trùng trong nhịp chỉ hỏi một lần.
 */
export function createBadgeBatcher(fetchBatch: (orderIds: string[]) => Promise<Response>) {
  let pending = new Map<string, Waiter[]>();
  let scheduled = false;

  function flush() {
    scheduled = false;
    const batch = pending;
    pending = new Map();
    const ids = [...batch.keys()];
    for (let i = 0; i < ids.length; i += MAX_BADGE_ORDERS) {
      const chunk = ids.slice(i, i + MAX_BADGE_ORDERS);
      fetchBatch(chunk).then(
        (res) => {
          for (const id of chunk) {
            const value: OrderBadge = {
              showSellerName: res.showSellerName,
              badge: res.badges[id] ?? null,
            };
            for (const w of batch.get(id) ?? []) w.resolve(value);
          }
        },
        (err: unknown) => {
          for (const id of chunk) for (const w of batch.get(id) ?? []) w.reject(err);
        },
      );
    }
  }

  return {
    load(orderId: string): Promise<OrderBadge> {
      return new Promise<OrderBadge>((resolve, reject) => {
        const list = pending.get(orderId);
        if (list) list.push({ resolve, reject });
        else pending.set(orderId, [{ resolve, reject }]);
        if (!scheduled) {
          scheduled = true;
          setTimeout(flush, 0);
        }
      });
    },
  };
}
